import { ESectionTrackStatus } from '@constants';
import { ArcElement, Chart as ChartJS, DoughnutController, Legend, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, useTheme } from 'styled-components';

import {
  CenterPercentage,
  ChartCenter,
  ChartNumber,
  ChartNumberWrapper,
  ChartPercentage,
  ChartTitle,
  ChartWrapper
} from './ProgressWithoutPlanificationChart.styles';

ChartJS.register(ArcElement, DoughnutController, Legend, Tooltip);

type Props = {
  subscriptionsTrackingStats: Record<ESectionTrackStatus, number>;
};

export const ProgressWithoutPlanificationChart: React.FC<Props> = ({
  subscriptionsTrackingStats
}) => {
  const { t } = useTranslation('StudentDashboardTranslations');
  const theme = useTheme() as DefaultTheme;

  const total = Object.values(subscriptionsTrackingStats).reduce((acc, curr) => acc + curr, 0);
  const percentage = total
    ? Math.round((subscriptionsTrackingStats[ESectionTrackStatus.KNOWN] / total) * 100)
    : 0;

  const data = {
    labels: [
      t(ESectionTrackStatus.NOT_STARTED),
      t(ESectionTrackStatus.KNOWN),
      t(ESectionTrackStatus.REVISED),
      t(ESectionTrackStatus.SUMMARISED),
      t(ESectionTrackStatus.STUDIED),
      t(ESectionTrackStatus.SEEN)
    ],
    datasets: [
      {
        data: [
          subscriptionsTrackingStats[ESectionTrackStatus.NOT_STARTED],
          subscriptionsTrackingStats[ESectionTrackStatus.KNOWN],
          subscriptionsTrackingStats[ESectionTrackStatus.REVISED],
          subscriptionsTrackingStats[ESectionTrackStatus.SUMMARISED],
          subscriptionsTrackingStats[ESectionTrackStatus.STUDIED],
          subscriptionsTrackingStats[ESectionTrackStatus.SEEN]
        ],
        backgroundColor: [
          theme.colors.black2,
          theme.colors.green6,
          theme.colors.orange6,
          theme.colors.purple6,
          theme.colors.black6,
          theme.colors.blue4
        ],
        borderWidth: 0,
        borderRadius: 20,
        spacing: -25
      }
    ]
  };

  const options = {
    responsive: false,
    maintainAspectRatio: false,
    elements: {
      arc: {
        borderJoinStyle: 'round'
      }
    },
    cutout: '85%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true
      }
    }
  } as const;

  return (
    <ChartCenter>
      <ChartWrapper>
        <Doughnut width={150} height={150} data={data} options={options} />
        <CenterPercentage>
          <ChartNumberWrapper>
            <ChartNumber>{percentage}</ChartNumber>
            <ChartPercentage>%</ChartPercentage>
          </ChartNumberWrapper>
          <ChartTitle>{t(ESectionTrackStatus.KNOWN)}</ChartTitle>
        </CenterPercentage>
      </ChartWrapper>
    </ChartCenter>
  );
};
